import { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Button, Paper, IconButton, Dialog, DialogContent, TextField, InputAdornment, MenuItem, Chip, Divider, Autocomplete, Alert, CircularProgress } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { LineChart } from '@mui/x-charts/LineChart';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import supabase from '../../supabaseClient';

const emptyForm = { name: '', type: 'produkt', default_price: '', min_price: '', stock_quantity: '' };

const currencyFormatter = new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' });
const dateFormatter = new Intl.DateTimeFormat('pl-PL', { dateStyle: 'medium', timeStyle: 'short' });

const emptyRecipeRow = () => ({ key: crypto.randomUUID(), component_id: null, quantity_per_unit: '' });

const typeStyles = {
    produkt: { bgcolor: '#E4EDFB', color: '#2E6DC4' },
    element: { bgcolor: '#F1EDE6', color: '#6B6B6B' },
    surowiec: { bgcolor: '#F7EEE3', color: '#C97A22' },
};

function ManagerProducts() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const [recipeRows, setRecipeRows] = useState([]);
    const [loadingRecipe, setLoadingRecipe] = useState(false);

    // Stany dla historii cen
    const [priceHistory, setPriceHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [selectedClientFilter, setSelectedClientFilter] = useState(null);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteErrorMsg, setDeleteErrorMsg] = useState('');

    const [idConfiguration, setIdConfiguration] = useState(null);

    // Stany dla okna dialogowego filtrów
    const [filterOpen, setFilterOpen] = useState(false);
    const [searchName, setSearchName] = useState('');
    const [searchType, setSearchType] = useState('');

    // Pobranie id_configuration zalogowanego użytkownika z jego profilu
    useEffect(() => {
        const getInitialData = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
                const { data: profileData } = await supabase
                    .from('profiles')
                    .select('id_configuration')
                    .eq('id', session.user.id)
                    .single();

                if (profileData) {
                    setIdConfiguration(profileData.id_configuration);
                }
            }
        };
        getInitialData();
    }, []);

    async function fetchProducts() {
        setLoading(true);
        const { data, error } = await supabase
            .from('products')
            .select('*')
            .eq('id_configuration', idConfiguration)
            .order('name');

        if (error) {
            console.error('Błąd pobierania produktów:', error);
            setLoading(false);
            return;
        }

        setRows(data);
        setLoading(false);
    }

    useEffect(() => {
        if (idConfiguration) fetchProducts();
    }, [idConfiguration]);

    // Pobieranie historii cen z joinem: order_items -> orders -> kontrahenci
    async function fetchPriceHistory(productId) {
        setLoadingHistory(true);
        setSelectedClientFilter(null);
        const { data, error } = await supabase
            .from('order_items')
            .select(`
                id,
                unit_price,
                created_at,
                order_id,
                orders (
                    id,
                    client_id,
                    kontrahenci (
                        id,
                        name
                    )
                )
            `)
            .eq('product_id', productId)
            .order('created_at', { ascending: true });

        if (error) {
            console.error('Błąd pobierania historii cen:', error);
            setPriceHistory([]);
        } else {
            const formattedData = (data || []).map((item) => {
                const clientObj = item.orders?.kontrahenci;
                const clientName = clientObj?.name || 'Nieznany kontrahent';

                return {
                    id: item.id,
                    order_id: item.order_id,
                    unit_price: Number(item.unit_price) || 0,
                    created_at: item.created_at,
                    date: new Date(item.created_at),
                    client_name: clientName,
                };
            });
            setPriceHistory(formattedData);
        }
        setLoadingHistory(false);
    }

    // Unikalna lista kontrahentów dla filtra w historii cen
    const clientOptions = useMemo(() => {
        const clients = priceHistory.map((item) => item.client_name);
        return Array.from(new Set(clients)).sort();
    }, [priceHistory]);

    // Przefiltrowana historia cen na podstawie wybranego kontrahenta
    const filteredPriceHistory = useMemo(() => {
        if (!selectedClientFilter) return priceHistory;
        return priceHistory.filter((item) => item.client_name === selectedClientFilter);
    }, [priceHistory, selectedClientFilter]);

    // Wyliczenia statystyk cenowych na podstawie przefiltrowanych danych
    const avgPrice = useMemo(() => {
        if (filteredPriceHistory.length === 0) return 0;
        const sum = filteredPriceHistory.reduce((acc, curr) => acc + curr.unit_price, 0);
        return sum / filteredPriceHistory.length;
    }, [filteredPriceHistory]);

    const elementOptions = rows.filter((row) => (row.type === 'element' || row.type === 'surowiec') && row.id !== editingId);

    const openAddDialog = () => {
        setEditingId(null);
        setForm(emptyForm);
        setRecipeRows([]);
        setPriceHistory([]);
        setSelectedClientFilter(null);
        setErrorMsg('');
        setDialogOpen(true);
    };

    const openEditDialog = async (row) => {
        setEditingId(row.id);
        setForm({
            name: row.name ?? '',
            type: row.type ?? 'produkt',
            default_price: String(row.default_price ?? ''),
            min_price: String(row.min_price ?? ''),
            stock_quantity: String(row.stock_quantity ?? ''),
        });
        setErrorMsg('');
        setDialogOpen(true);

        fetchPriceHistory(row.id);

        if (row.type === 'produkt') {
            setLoadingRecipe(true);
            const { data, error } = await supabase
                .from('product_recipe')
                .select('id, component_id, quantity_per_unit')
                .eq('product_id', row.id);

            if (error) {
                console.error('Błąd pobierania receptury:', error);
            } else {
                setRecipeRows(
                    data.map((r) => ({ key: r.id, component_id: r.component_id, quantity_per_unit: String(r.quantity_per_unit) }))
                );
            }
            setLoadingRecipe(false);
        } else {
            setRecipeRows([]);
        }
    };

    const handleFieldChange = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleAddRecipeRow = () => {
        setRecipeRows((prev) => [...prev, emptyRecipeRow()]);
    };

    const handleRemoveRecipeRow = (key) => {
        setRecipeRows((prev) => prev.filter((r) => r.key !== key));
    };

    const handleRecipeComponentChange = (key, component) => {
        setRecipeRows((prev) =>
            prev.map((r) => (r.key === key ? { ...r, component_id: component?.id ?? null } : r))
        );
    };

    const handleRecipeQuantityChange = (key, value) => {
        setRecipeRows((prev) => prev.map((r) => (r.key === key ? { ...r, quantity_per_unit: value } : r)));
    };

    const handleSave = async () => {
        if (!form.name.trim()) {
            setErrorMsg('Nazwa produktu jest wymagana.');
            return;
        }

        if (!idConfiguration) {
            setErrorMsg('Nie udało się ustalić konfiguracji użytkownika — odśwież stronę i spróbuj ponownie.');
            return;
        }

        const cleanRecipeRows = recipeRows.filter((r) => r.component_id && Number(r.quantity_per_unit) > 0);

        if (form.type === 'produkt' && recipeRows.some((r) => !r.component_id || !(Number(r.quantity_per_unit) > 0))) {
            setErrorMsg('Każda pozycja receptury musi mieć wybrany element i ilość większą od zera. Usuń puste wiersze albo je uzupełnij.');
            return;
        }

        setSaving(true);
        setErrorMsg('');

        const payload = {
            name: form.name.trim(),
            type: form.type,
            default_price: Number(form.default_price) || 0,
            min_price: Number(form.min_price) || 0,
            stock_quantity: Number(form.stock_quantity) || 0,
        };

        let productId = editingId;

        if (editingId) {
            const { error } = await supabase.from('products').update(payload).eq('id', editingId);
            if (error) {
                console.error('Błąd zapisu produktu:', error);
                setErrorMsg('Nie udało się zapisać produktu.');
                setSaving(false);
                return;
            }
        } else {
            const { data, error } = await supabase
                .from('products')
                .insert({ ...payload, id_configuration: idConfiguration })
                .select()
                .single();

            if (error) {
                console.error('Błąd zapisu produktu:', error);
                setErrorMsg('Nie udało się zapisać produktu.');
                setSaving(false);
                return;
            }
            productId = data.id;
        }

        const { error: deleteRecipeError } = await supabase.from('product_recipe').delete().eq('product_id', productId);

        if (deleteRecipeError) {
            console.error('Błąd czyszczenia receptury:', deleteRecipeError);
            setErrorMsg('Produkt zapisany, ale nie udało się zaktualizować receptury.');
            setSaving(false);
            return;
        }

        if (form.type === 'produkt' && cleanRecipeRows.length > 0) {
            const recipePayload = cleanRecipeRows.map((r) => ({
                product_id: productId,
                component_id: r.component_id,
                quantity_per_unit: Number(r.quantity_per_unit),
                id_configuration: idConfiguration,
            }));

            const { error: insertRecipeError } = await supabase.from('product_recipe').insert(recipePayload);

            if (insertRecipeError) {
                console.error('Błąd zapisu receptury:', insertRecipeError);
                setErrorMsg('Produkt zapisany, ale nie udało się zaktualizować receptury.');
                setSaving(false);
                return;
            }
        }

        setSaving(false);
        setDialogOpen(false);
        fetchProducts();
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;

        setDeleting(true);
        setDeleteErrorMsg('');

        const { error } = await supabase.from('products').delete().eq('id', deleteTarget.id);
        
        setDeleting(false);

        if (error) {
            console.error('Błąd usuwania produktu:', error);
            setDeleteErrorMsg('Nie można usunąć produktu, ponieważ jest on powiązany z zamówieniami lub recepturami w systemie.');
            return;
        }

        setDeleteTarget(null);
        setDeleteErrorMsg('');
        fetchProducts();
    };

    const filteredRows = rows.filter((row) => {
        const matchesName = searchName === '' || (row.name && row.name.toLowerCase().includes(searchName.toLowerCase()));
        const matchesType = searchType === '' || row.type === searchType;

        return matchesName && matchesType;
    });

    const columns = [
        { field: 'name', headerName: 'NAZWA', flex: 1.4, minWidth: 180, renderCell: (params) => <strong>{params.value}</strong> },
        {
            field: 'type',
            headerName: 'TYP',
            flex: 0.8,
            minWidth: 110,
            renderCell: (params) => (
                <Chip
                    label={params.value === 'element' ? 'Element' : params.value === 'surowiec' ? 'Surowiec' : 'Produkt'}
                    size="small"
                    sx={{ ...(typeStyles[params.value] ?? typeStyles.produkt), fontWeight: 600, borderRadius: '8px', height: 26 }}
                />
            ),
        },
        {
            field: 'default_price',
            headerName: 'CENA DOMYŚLNA',
            flex: 1,
            minWidth: 140,
            renderCell: (params) => currencyFormatter.format(params.value ?? 0),
        },
        {
            field: 'min_price',
            headerName: 'CENA MIN.',
            flex: 1,
            minWidth: 120,
            renderCell: (params) => currencyFormatter.format(params.value ?? 0),
        },
        { field: 'stock_quantity', headerName: 'NA STANIE', flex: 0.9, minWidth: 110 },
        {
            field: 'actions',
            headerName: '',
            flex: 0.6,
            minWidth: 90,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditDialog(params.row); }}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteTarget(params.row); setDeleteErrorMsg(''); }}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    const priceHistoryColumns = [
        { field: 'client_name', headerName: 'KONTRAHENT', flex: 1.8, minWidth: 180 },
        {
            field: 'unit_price',
            headerName: 'CENA JEDN.',
            flex: 1,
            minWidth: 110,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                    {currencyFormatter.format(params.value ?? 0)}
                </Typography>
            ),
        },
        {
            field: 'created_at',
            headerName: 'DATA',
            flex: 1.2,
            minWidth: 140,
            renderCell: (params) => dateFormatter.format(new Date(params.value)),
        },
    ];

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: 'primary.main' }}>Produkty</Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setFilterOpen(true)}
                        startIcon={<FilterListIcon />}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2, py: 1 }}
                    >
                        Filtry
                    </Button>
                    <Button
                        variant="contained"
                        onClick={openAddDialog}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5, py: 1 }}
                    >
                        + Nowy produkt
                    </Button>
                </Box>
            </Box>

            <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #ECE7DE' }}>
                <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    loading={loading}
                    onRowClick={(params) => openEditDialog(params.row)}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 20, page: 0 },
                        },
                    }}
                    pageSizeOptions={[]}
                    columnHeaderHeight={44}
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-row': { cursor: 'pointer' },
                        '& .MuiDataGrid-columnHeaders': { backgroundColor: '#FAF7F2' },
                        '& .MuiDataGrid-columnHeaderTitle': { color: 'text.secondary', fontWeight: 700, fontSize: '12px', letterSpacing: '0.03em' },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-row:hover': { backgroundColor: '#FEFCF9' },
                        
                        // Ukrycie etykiety "Rows per page:" oraz selektora opcji
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                            display: 'none !important',
                        },
                    }}
                />
            </Paper>

            {/* Dodawanie / edycja */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogContent sx={{ p: 4 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'primary.main', mb: 3 }}>
                        {editingId ? 'Edytuj produkt' : 'Nowy produkt'}
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Nazwa" value={form.name} onChange={handleFieldChange('name')} size="small" fullWidth />
                            <TextField
                                select
                                label="Typ"
                                value={form.type}
                                onChange={handleFieldChange('type')}
                                size="small"
                                sx={{ minWidth: 160 }}
                            >
                                <MenuItem value="produkt">Produkt</MenuItem>
                                <MenuItem value="element">Element</MenuItem>
                                <MenuItem value="surowiec">Surowiec</MenuItem>
                            </TextField>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Cena domyślna"
                                type="number"
                                value={form.default_price}
                                onChange={handleFieldChange('default_price')}
                                size="small"
                                fullWidth
                                InputProps={{ endAdornment: <InputAdornment position="end">zł</InputAdornment> }}
                            />
                            <TextField
                                label="Cena minimalna"
                                type="number"
                                value={form.min_price}
                                onChange={handleFieldChange('min_price')}
                                size="small"
                                fullWidth
                                InputProps={{ endAdornment: <InputAdornment position="end">zł</InputAdornment> }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Na stanie"
                                type="number"
                                value={form.stock_quantity}
                                onChange={handleFieldChange('stock_quantity')}
                                size="small"
                                fullWidth
                            />
                            <Box sx={{ flex: 1 }} />
                        </Box>
                    </Box>

                    {form.type === 'produkt' && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 1 }}>
                                Receptura — z jakich elementów składa się ten produkt
                            </Typography>

                            {loadingRecipe ? (
                                <Typography sx={{ color: 'text.secondary', fontSize: '13px', mb: 2 }}>Ładowanie receptury...</Typography>
                            ) : (
                                <Box sx={{ bgcolor: '#FAF7F2', borderRadius: 2, p: 2, mb: 1.5 }}>
                                    {recipeRows.length === 0 && (
                                        <Typography sx={{ color: 'text.secondary', fontSize: '13px', mb: 1 }}>
                                            Brak zdefiniowanych elementów.
                                        </Typography>
                                    )}
                                    {recipeRows.map((r) => (
                                        <Box key={r.key} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
                                            <Autocomplete
                                                sx={{ flex: 1.4, bgcolor: 'background.paper', borderRadius: 1 }}
                                                options={elementOptions}
                                                getOptionLabel={(option) => option.name ?? ''}
                                                value={elementOptions.find((o) => o.id === r.component_id) ?? null}
                                                onChange={(_, value) => handleRecipeComponentChange(r.key, value)}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                renderInput={(params) => <TextField {...params} placeholder="Wybierz element" size="small" />}
                                            />
                                            <TextField
                                                size="small"
                                                type="number"
                                                placeholder="Ilość na szt."
                                                value={r.quantity_per_unit}
                                                onChange={(e) => handleRecipeQuantityChange(r.key, e.target.value)}
                                                sx={{ flex: 0.7, bgcolor: 'background.paper', borderRadius: 1 }}
                                            />
                                            <IconButton size="small" onClick={() => handleRemoveRecipeRow(r.key)}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            <Button
                                fullWidth
                                onClick={handleAddRecipeRow}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    border: '1px dashed #D8D2C7',
                                    borderRadius: 2,
                                    py: 1,
                                    mb: 2,
                                }}
                            >
                                + dodaj element do receptury
                            </Button>
                        </>
                    )}

                    {/* Sekcja Raportu Historii Cen */}
                    {editingId && (
                        <>
                            <Divider sx={{ my: 3 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'primary.main' }}>
                                    Historia cen sprzedaży
                                </Typography>
                                {priceHistory.length > 0 && (
                                    <Autocomplete
                                        size="small"
                                        options={clientOptions}
                                        value={selectedClientFilter}
                                        onChange={(_, newValue) => setSelectedClientFilter(newValue)}
                                        sx={{ width: 250 }}
                                        renderInput={(params) => <TextField {...params} placeholder="Filtruj po kontrahencie" />}
                                    />
                                )}
                            </Box>

                            {loadingHistory ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress size={30} />
                                </Box>
                            ) : priceHistory.length === 0 ? (
                                <Typography sx={{ color: 'text.secondary', fontSize: '13px', my: 2 }}>
                                    Brak historii sprzedaży dla tego produktu.
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, my: 2 }}>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Paper elevation={0} sx={{ flex: 1, p: 2, bgcolor: '#FAF7F2', borderRadius: 2, border: '1px solid #ECE7DE' }}>
                                            <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontWeight: 600 }}>
                                                ŚREDNIA CENA {selectedClientFilter ? `(${selectedClientFilter})` : ''}
                                            </Typography>
                                            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: 'primary.main', mt: 0.5 }}>
                                                {currencyFormatter.format(avgPrice)}
                                            </Typography>
                                        </Paper>
                                        <Paper elevation={0} sx={{ flex: 1, p: 2, bgcolor: '#FAF7F2', borderRadius: 2, border: '1px solid #ECE7DE' }}>
                                            <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontWeight: 600 }}>
                                                Liczba transakcji
                                            </Typography>
                                            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: 'text.primary', mt: 0.5 }}>
                                                {filteredPriceHistory.length}
                                            </Typography>
                                        </Paper>
                                    </Box>

                                    {filteredPriceHistory.length > 0 ? (
                                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#FEFCF9', borderRadius: 2, border: '1px solid #ECE7DE' }}>
                                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                                                Trendy cenowe w czasie
                                            </Typography>
                                            <Box sx={{ width: '100%', height: 220 }}>
                                                <LineChart
                                                    dataset={filteredPriceHistory}
                                                    xAxis={[
                                                        {
                                                            dataKey: 'date',
                                                            scaleType: 'time',
                                                            valueFormatter: (date) => dateFormatter.format(date),
                                                        },
                                                    ]}
                                                    series={[
                                                        {
                                                            dataKey: 'unit_price',
                                                            label: 'Cena jednostkowa (zł)',
                                                            color: '#2E6DC4',
                                                            showMark: true,
                                                        },
                                                    ]}
                                                    height={220}
                                                    margin={{ left: 50, right: 20, top: 20, bottom: 30 }}
                                                />
                                            </Box>
                                        </Paper>
                                    ) : (
                                        <Typography sx={{ color: 'text.secondary', fontSize: '13px', my: 1 }}>
                                            Brak transakcji dla wybranego kontrahenta.
                                        </Typography>
                                    )}

                                    <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #ECE7DE', overflow: 'hidden' }}>
                                        <DataGrid
                                            rows={filteredPriceHistory}
                                            columns={priceHistoryColumns}
                                            autoHeight
                                            density="compact"
                                            initialState={{
                                                pagination: { paginationModel: { pageSize: 5, page: 0 } },
                                            }}
                                            pageSizeOptions={[]}
                                            disableRowSelectionOnClick
                                            disableColumnMenu
                                            columnHeaderHeight={36}
                                            sx={{
                                                border: 'none',
                                                '& .MuiDataGrid-columnHeaders': { backgroundColor: '#FAF7F2' },
                                                '& .MuiDataGrid-columnHeaderTitle': { color: 'text.secondary', fontWeight: 700, fontSize: '11px' },
                                                '& .MuiDataGrid-cell': { fontSize: '13px' },
                                                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                                                    display: 'none !important',
                                                },
                                            }}
                                        />
                                    </Paper>
                                </Box>
                            )}
                        </>
                    )}

                    {errorMsg && <Typography sx={{ color: '#C7433F', fontSize: '13px', my: 2 }}>{errorMsg}</Typography>}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
                        <Button onClick={() => setDialogOpen(false)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            Anuluj
                        </Button>
                        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            {saving ? 'Zapisywanie...' : 'Zapisz'}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Okno dialogowe filtrów */}
            <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogContent sx={{ p: 4 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'primary.main', mb: 3 }}>
                        Filtry
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                        <TextField
                            label="Nazwa"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                        />
                        <TextField
                            select
                            label="Typ"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={searchType}
                            onChange={(e) => setSearchType(e.target.value)}
                        >
                            <MenuItem value="">Wszystkie</MenuItem>
                            <MenuItem value="produkt">Produkt</MenuItem>
                            <MenuItem value="element">Element</MenuItem>
                            <MenuItem value="surowiec">Surowiec</MenuItem>
                        </TextField>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                        <Button
                            onClick={() => {
                                setSearchName('');
                                setSearchType('');
                            }}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}
                        >
                            Wyczyść
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => setFilterOpen(false)}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 3 }}
                        >
                            Zamknij
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Okno dialogowe potwierdzenia usunięcia */}
            <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogContent sx={{ p: 4 }}>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: 'primary.main', mb: 1 }}>
                        Usuń produkt
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: 'text.secondary', mb: 3 }}>
                        Czy na pewno chcesz usunąć produkt <strong>{deleteTarget?.name}</strong>? Ta operacja jest nieodwracalna.
                    </Typography>

                    {deleteErrorMsg && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                            {deleteErrorMsg}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button
                            onClick={() => setDeleteTarget(null)}
                            disabled={deleting}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}
                        >
                            Anuluj
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleDelete}
                            disabled={deleting}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}
                        >
                            {deleting ? 'Usuwanie...' : 'Usuń'}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default ManagerProducts;